import React, { ReactNode } from 'react';
import { Grid, makeStyles, useMediaQuery } from 'housecall-ui';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';

import { Country } from '../../api/countryShortcut';
import { AutocompleteField } from '../../components/form/AutocompleteField';
import { FormField } from '../../components/form/FormField';

import { CA_STATES_LIST, CANADA, COUNTRY_LIST, FieldName, getPlaceholdersByCountry, US_STATES_LIST } from './consts';
import { OnlineBookingAutoComplete } from './OnlineBookingAutoComplete';

type Props = {
  country: Country;
  children?: ReactNode | null;
  isLeadForm?: boolean;
};

const useStyles = makeStyles((theme: HCPTheme) => ({
  addressSection: {
    paddingBottom: theme.spacing(2),
  },
  formFieldGrid: {},
}));

export const AddressFields = ({ country, children, isLeadForm }: Props) => {
  const matches = useMediaQuery('(max-width:450px)', { noSsr: true });
  const divideInputs = isLeadForm && !matches;
  const { addressSection } = useStyles();

  const gridArgs = {
    xs: divideInputs ? (6 as const) : (12 as const),
    md: 6 as const,
  };

  return (
    <Grid container spacing={1} className={addressSection}>
      <Grid item {...gridArgs}>
        <OnlineBookingAutoComplete />
      </Grid>
      <Grid item {...gridArgs}>
        <FormField<FieldName>
          name="addressLine2"
          data-testid="online-booking-contact-unit"
          label="Unit / apartment / suite"
          fullWidth
          autoComplete="address-line2"
        />
      </Grid>

      <Grid item {...gridArgs}>
        <FormField<FieldName>
          name="city"
          data-testid="online-booking-contact-city"
          label="City *"
          fullWidth
          autoComplete="address-level2"
        />
      </Grid>
      <Grid item {...gridArgs}>
        <FormField<FieldName>
          name="zipCode"
          data-testid="online-booking-contact-zip"
          label={getPlaceholdersByCountry(country).zipCode}
          fullWidth
          autoComplete="postal-code"
        />
      </Grid>
      <Grid item {...gridArgs}>
        <AutocompleteField
          label="Country *"
          name="country"
          /* Broken Types for Autocomplete */
          options={COUNTRY_LIST as unknown as string[]}
          ListboxProps={{
            'data-testid': 'online-booking-contact-country-list',
          }}
          InputProps={{
            autoComplete: 'address-level1',
            'data-testid': 'online-booking-contact-country',
          }}
        />
      </Grid>
      <Grid item {...gridArgs}>
        <AutocompleteField
          label={getPlaceholdersByCountry(country).state}
          name="state"
          options={country === CANADA ? CA_STATES_LIST : US_STATES_LIST}
          ListboxProps={{
            'data-testid': 'online-booking-contact-state-list',
          }}
          InputProps={{
            autoComplete: 'address-level1',
            'data-testid': 'online-booking-contact-state',
          }}
        />
      </Grid>
      {children || null}
    </Grid>
  );
};
